import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
} from '@mui/material';
import { 
  WbSunnyOutlined as SunIcon, 
  ModeNightOutlined as MoonIcon,
  LocalFireDepartment as FlameIcon 
} from '@mui/icons-material';
import DailyStreakMode from "Assets/Images/DailyStreakMode.png";
import WeeklyStreakMode from "Assets/Images/WeeklyStreakMode.png";
import { useDispatch, useSelector } from 'react-redux';
import { updateStreakMode } from 'Slices/thunks';

// --- Reusable Card Component ---
const StreakModeCard = ({
  title,
  isActive,
  earnText,
  features,
  illustration,
  isSelected,
  onClick,
  borderColor,
  earnTextColor,
}) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 2,
      borderRadius: '8px',
      border: `2px solid ${isSelected ? '#007BFF' : '#E0E0E0'}`,
      cursor: 'pointer',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'border-color 0.2s ease-in-out',
    }}
  >
    {illustration}
    <Box display="flex" alignItems="center" gap={1} mt={2}>
      <Typography variant="h6" fontWeight="bold">
        {title}
      </Typography>
      {isActive && <Chip label="Currently Active" size="small" sx={{ bgcolor: '#E8F5E9', color: '#4CAF50', fontWeight: 'medium' }} />}
    </Box>
    <Typography
      variant="body2"
      fontWeight="bold"
      sx={{
        border: `1px solid ${borderColor}`,
        color: earnTextColor,
        borderRadius: '8px',
        py: 1,
        px: 2,
        my: 2,
        textAlign: 'center',
      }}
    >
      {earnText}
    </Typography>
    <List sx={{ p: 0 }}>
      {features.map((feature, index) => (
        <ListItem key={index} disableGutters sx={{ p: 0 }}>
          <ListItemIcon sx={{ minWidth: '24px', color: '#007BFF' }}>
            ◆
          </ListItemIcon>
          <ListItemText 
            primary={
              <Box>
                <Typography variant="body2" color="secondary.main" fontSize={"14px"} fontWeight={700}>
                  {feature?.split(':')[0]}:
                </Typography>
                <Typography variant="body2" color="secondary.light" fontSize={"14px"} fontWeight={500}>
                  {feature?.split(':')[1]}
                </Typography>

                {/* <Typography variant="body2" color="text.secondary">
                  <span style={{ fontWeight: 'bold', color: '#333' }}>{feature.split(':')[0]}:</span>
                  {feature.split(':')[1]}
                </Typography> */}
              </Box>
            } 
          />
        </ListItem>
      ))}
    </List>
  </Paper>
);

// --- Illustrations ---
const DailyIllustration = () => (
  <Box sx={{ bgcolor: '#F7F8FA', p: 2, borderRadius: '8px', textAlign: 'center' }}>
    {/* <Box display="flex" justifyContent="space-between" alignItems="center" px={4} mb={2}>
      <SunIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
      <MoonIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
    </Box> */}
    {/* Replace with your actual image asset */}
    <img src={DailyStreakMode} alt="Person coding daily" style={{ width: '100%', objectFit: 'contain' }} />
  </Box>
);

const WeeklyIllustration = () => {
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <Box sx={{ bgcolor: '#F7F8FA', p: 2, borderRadius: '8px', textAlign: 'center' }}>
      {/* <Box display="flex" justifyContent="space-around" alignItems="center" mb={1}>
        {days.map((day, i) => <Typography key={i} variant="caption" color="text.secondary">{day}</Typography>)}
      </Box> */}
      {/* <Box sx={{ bgcolor: 'white', border: '1px solid #EEE', borderRadius: '20px', p: 0.5, display: 'flex', justifyContent: 'space-around', alignItems: 'center', mb: 2 }}>
        {days.map((_, i) => <FlameIcon key={i} sx={{ color: '#FFA726', fontSize: 20 }} />)}
      </Box> */}
      {/* Replace with your actual image asset */}
      <img src={WeeklyStreakMode} alt="Person coding weekly" style={{ width: '100%', objectFit: 'contain' }} />
    </Box>
  );
};

// --- Main Modal Component ---
const StreakModeModal = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const [selectedMode, setSelectedMode] = useState('daily');
  const [isChecked, setIsChecked] = useState(false);

  const { Profile, Translation } = useSelector((state) => ({
    Profile: state.Profile.profile,
    Translation: state.Translation.translation?.Dashboard || {},
  }));

  const handleStreakModeChange = (value) => {
    setSelectedMode(value);
    setIsChecked(false);
  }

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const handleSubmit = () => {
    const isWeekly = selectedMode === 'daily' ? false : true;
    dispatch(updateStreakMode({ IsWeekly: isWeekly }));
    setOpen(false);
  }

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (Profile) {
      setSelectedMode(Profile?.IsWeekly ? 'weekly' : 'daily')
    }

  }, [Profile])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StreakModeCard
              title={Translation?.DailyStreakMode}
              isActive={selectedMode === 'daily'}
              earnText={Translation?.EarnDailyStreak}
              features={[
                Translation?.DailyModeDescription,
                Translation?.DailyStreakCredit,
              ]}
              illustration={<DailyIllustration />}
              isSelected={selectedMode === 'daily'}
              onClick={() => handleStreakModeChange('daily')}
              borderColor="#D1C4E9"
              earnTextColor="#673AB7"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StreakModeCard
              title={Translation?.WeeklyStreakMode}
              isActive={selectedMode === 'weekly'}
              earnText={Translation?.EarnWeeklyStreak}
              features={[
                Translation?.WeeklyModeDescription,
                Translation?.WeeklyStreakCredit
              ]}
              illustration={<WeeklyIllustration />}
              isSelected={selectedMode === 'weekly'}
              onClick={() => handleStreakModeChange('weekly')}
              borderColor="#FFD180"
              earnTextColor="#FF9800"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 3, justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControlLabel
          control={<Checkbox checked={isChecked} onChange={handleCheckboxChange} />}
          label={selectedMode === 'daily' ? Translation?.ContinueDailyStreakMode : Translation?.ContinueWeeklyMode}
          // sx={{ visibility: selectedMode === 'daily' ? 'visible' : 'hidden' }}
        />
        <Box>
          <Button onClick={handleClose} sx={{ color: 'secondary.light', mr: 1 }}>{Translation?.Close}</Button>
          <Button 
            variant="contained" 
            disabled={!isChecked}
            onClick={handleSubmit}
            sx={{
              bgcolor: isChecked ? 'primary.main' : 'secondary.light',
              color: isChecked ? 'white' : 'secondary.light',
              '&:hover': {
                bgcolor: isChecked ? 'primary.main' : 'secondary.light',
              },
            }}
          >
            {Translation?.SelectMode}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default StreakModeModal;